import React from "react";
import { X, Mail, Phone, User, Truck, Package, BadgeCheck } from "lucide-react";
import { approvedForCategory } from "../../lib/suppliers";

const SupplierCard = ({ s }) => (
  <div className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`approved-supplier-${s.id}`}>
    <div className="font-semibold text-slate-900 flex items-center gap-2">
      {s.name}
      {s.all_categories && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">All categories</span>}
      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-normal">{(s.types || []).map((t) => (t === "materials" ? "Parts & Materials" : "Access Hire")).join(" · ")}</span>
    </div>
    <div className="mt-1.5 space-y-1 text-sm text-slate-700">
      {s.contact_name && <div className="flex items-center gap-2"><User size={13} className="text-slate-400" />{s.contact_name}</div>}
      {s.email && <a href={`mailto:${s.email}`} className="flex items-center gap-2 text-blue-700 hover:underline"><Mail size={13} />{s.email}</a>}
      {s.phone && <a href={`tel:${s.phone.replace(/\s+/g, "")}`} className="flex items-center gap-2 text-blue-700 hover:underline"><Phone size={13} />{s.phone}</a>}
      {s.notes && <div className="text-xs text-slate-500 whitespace-pre-line">{s.notes}</div>}
    </div>
  </div>
);

const Group = ({ title, icon: Icon, items, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-slate-700">
      <Icon size={14} />{title} <span className="text-slate-400">({items.length})</span>
    </div>
    {items.length ? items.map((s) => <SupplierCard key={s.id} s={s} />) : <div className="text-sm text-slate-400">None approved.</div>}
  </div>
);

export function ApprovedSuppliersPanel({ open, onClose, suppliers, category }) {
  if (!open) return null;
  const g = approvedForCategory(suppliers, category);
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="approved-suppliers-panel">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative w-full max-w-md h-full bg-slate-50 shadow-2xl overflow-y-auto slide-in-right">
        <div className="sticky top-0 bg-slate-900 text-white px-5 py-4 flex items-start justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-white/60">Approved suppliers</div>
            <div className="font-display text-xl font-black">{category || "No category picked"}</div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="approved-suppliers-close"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-6">
          <Group title="Parts & Materials" icon={Package} items={g.materials} testid="approved-suppliers-materials" />
          <Group title="Access Hire" icon={Truck} items={g.access_hire} testid="approved-suppliers-access-hire" />
        </div>
      </div>
    </div>
  );
}

export function ApprovedSuppliersButton({ onClick, disabled }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid="view-approved-suppliers-btn"
      className="mt-1.5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-blue-200 bg-blue-50 text-[11px] font-semibold uppercase tracking-wider text-blue-700 hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
      <BadgeCheck size={12} /> View approved suppliers
    </button>
  );
}
